/**
 * Resources People register overlay — Soft≠Live.
 * Soft practice may keep labels on soft-sync. Live register labels write via
 * PATCH /personnel/{u}/profile (desk). Live browser overlay is cleared — never
 * Soft-invent profile SoR after Live path exists.
 */
import {
  softPeopleRowsOf,
  softSyncSubscribe,
  softPeopleRegisterSave,
} from "./response-soft-sync.js";

export const PEOPLE_REGISTER_EVENT = "croc-resources-people-register";
export const PEOPLE_REGISTER_STORAGE = "croc.resources.people.register";

const LABEL_KEYS = ["name", "role", "site", "phone", "phoneSay", "phoneRegion", "callsign", "notes"];
/* Soft≠Live: crew + Live profile stay off browser-seat overlay. */
const EDIT_KEYS = LABEL_KEYS;

function cloneMap(raw) {
  const out = Object.create(null);
  if (!raw || typeof raw !== "object") return out;
  for (const [k, v] of Object.entries(raw)) {
    const user = String(k || "").trim();
    if (!user || !v || typeof v !== "object") continue;
    out[user] = { ...v, user };
  }
  return out;
}

function loadLiveOverlay() {
  try {
    const raw = localStorage.getItem(PEOPLE_REGISTER_STORAGE);
    if (!raw) return Object.create(null);
    return cloneMap(JSON.parse(raw));
  } catch {
    return Object.create(null);
  }
}

function persistLiveOverlay(map) {
  try {
    localStorage.setItem(PEOPLE_REGISTER_STORAGE, JSON.stringify(map));
  } catch {
    /* ignore */
  }
  try {
    window.dispatchEvent(new CustomEvent(PEOPLE_REGISTER_EVENT, { detail: { soft: false, map } }));
  } catch {
    /* ignore */
  }
}

function phoneSayOf(phone, region) {
  const p = String(phone || "").trim();
  const r = String(region || "").trim();
  if (!p) return "No phone on file";
  return r ? `${p} · ${r}` : p;
}

/** Merge Soft overlay only. Live rows already carry profile SoR from GET /personnel. */
export function applyPeopleRegisterOverlay(rows, opts) {
  const list = Array.isArray(rows) ? rows : [];
  const soft = !!(opts && opts.soft);
  if (!soft) {
    /* Purge legacy Soft seat overlays so Live profile is the only SoR. */
    try {
      if (localStorage.getItem(PEOPLE_REGISTER_STORAGE)) {
        localStorage.removeItem(PEOPLE_REGISTER_STORAGE);
        window.dispatchEvent(new CustomEvent(PEOPLE_REGISTER_EVENT, { detail: { soft: false, cleared: true } }));
      }
    } catch {
      /* ignore */
    }
    return list.map((row) => (row && typeof row === "object" ? { ...row } : row));
  }
  const overlay = loadLiveOverlay();
  return list.map((row) => {
    if (!row || typeof row !== "object") return row;
    const user = String(row.user || "").trim();
    if (!user) return row;
    const patch = overlay[user];
    if (!patch) return { ...row };
    const next = { ...row };
    for (const k of EDIT_KEYS) {
      if (patch[k] != null && String(patch[k]).trim() !== "") next[k] = patch[k];
    }
    if (patch.phone != null) {
      next.phone = String(patch.phone || "");
      next.phoneSay = phoneSayOf(next.phone, next.phoneRegion || patch.phoneRegion);
    }
    next.registerEdited = true;
    return next;
  });
}

export function peopleRegisterFieldsOf(row) {
  const r = row && typeof row === "object" ? row : {};
  return {
    user: String(r.user || "").trim(),
    name: String(r.name || "").trim(),
    role: String(r.role || "").trim(),
    site: String(r.site || "").trim(),
    phone: String(r.phone || "").trim(),
    phoneRegion: String(r.phoneRegion || "").trim(),
    callsign: String(r.callsign || "").trim(),
    notes: String(r.notes || "").trim(),
    crew: String(r.crew || "").trim(),
  };
}

/**
 * Soft → soft-sync. Live → desk must PATCH profile/crew; this clears seat Soft.
 */
export function savePeopleRegister(user, fields, opts) {
  const uid = String(user || "").trim();
  if (!uid) return { ok: false, reason: "user_required" };
  const soft = !!(opts && opts.soft);
  const incoming = fields && typeof fields === "object" ? fields : {};
  const patch = {
    user: uid,
    name: String(incoming.name || "").trim(),
    role: String(incoming.role || "").trim(),
    site: String(incoming.site || "").trim(),
    phone: String(incoming.phone || "").trim(),
    phoneRegion: String(incoming.phoneRegion || "").trim(),
    callsign: String(incoming.callsign || "").trim(),
    notes: String(incoming.notes || "").trim(),
    phoneSay: phoneSayOf(incoming.phone, incoming.phoneRegion),
    revisedAt: Date.now(),
  };

  if (soft) {
    patch.crew = String(incoming.crew || "").trim();
    softPeopleRegisterSave(uid, patch);
    return { ok: true, soft: true, user: uid, patch };
  }

  /* Live: refuse Soft invent — clear any legacy overlay for this user. */
  const map = loadLiveOverlay();
  if (map[uid]) {
    delete map[uid];
    persistLiveOverlay(map);
  }
  return { ok: true, soft: false, live_api: true, user: uid, patch };
}

export function peopleRegisterSubscribe(cb) {
  if (typeof cb !== "function" || typeof window === "undefined") return () => {};
  const onLive = () => {
    try { cb({ soft: false }); } catch { /* ignore */ }
  };
  const onSoft = () => {
    try { cb({ soft: true }); } catch { /* ignore */ }
  };
  window.addEventListener(PEOPLE_REGISTER_EVENT, onLive);
  const offSoft = softSyncSubscribe(onSoft);
  return () => {
    window.removeEventListener(PEOPLE_REGISTER_EVENT, onLive);
    offSoft && offSoft();
  };
}

export function softPeopleRegisterRowsOf() {
  return softPeopleRowsOf().map((r) => ({ ...r }));
}
